"use client";

import { type User as FirebaseUser, onAuthStateChanged } from "firebase/auth";
import Cookies from "js-cookie";
import { usePostHog } from "posthog-js/react";
import {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useState,
} from "react";
import { type User, usersService } from "@/lib/api";
import { authService } from "@/lib/firebase/auth";
import { auth } from "@/lib/firebase/client";
import { fetchWithRetry } from "../utils";

// Define types
interface AuthContextType {
	currentUser: FirebaseUser | null;
	backendUser: User | null;
	loading: boolean;
	token: string | null;
	signOut: () => Promise<void>;
	getUserToken: () => Promise<string | null>;
	refreshUserData: () => Promise<void>;
}

// Create context with default values
const AuthContext = createContext<AuthContextType>({
	currentUser: null,
	backendUser: null,
	loading: true,
	token: null,
	signOut: async () => {},
	getUserToken: async () => null,
	refreshUserData: async () => {},
});

// Provider component
export function AuthProvider({ children }: { children: React.ReactNode }) {
	const [currentUser, setCurrentUser] = useState<FirebaseUser | null>(null);
	const [backendUser, setBackendUser] = useState<User | null>(null);
	const [loading, setLoading] = useState(true);
	const [token, setToken] = useState<string | null>(null);
	const posthog = usePostHog();

	// Function to get user token
	const getUserToken = async (): Promise<string | null> => {
		if (!currentUser) return null;
		try {
			const token = await currentUser.getIdToken(true);
			setToken(token);
			return token;
		} catch (error) {
			console.error("Error getting token:", error);
			return null;
		}
	};

	// Function to sync language preference with NEXT_LOCALE cookie
	const syncLanguagePreference = useCallback(
		(preferredLanguage: string | null | undefined) => {
			if (!preferredLanguage) {
				return;
			}

			// Get current locale from cookie using js-cookie
			const currentLocaleCookie = Cookies.get("NEXT_LOCALE");

			// If user's preferred language differs from cookie, update the cookie
			if (preferredLanguage !== currentLocaleCookie) {
				console.log(
					`Language sync: updating cookie from ${currentLocaleCookie || "unset"} to ${preferredLanguage}`,
				);

				// Set the cookie with js-cookie (expires in 1 year)
				Cookies.set("NEXT_LOCALE", preferredLanguage, {
					expires: 365, // 365 days
					path: "/",
					sameSite: "lax",
				});

				// Refresh the page to apply the new locale after a short delay
				setTimeout(() => {
					if (typeof window !== "undefined") {
						window.location.reload();
					}
				}, 100);
			}
		},
		[],
	);

	// Function to fetch user data from backend
	const fetchUserData = useCallback(
		async (user: FirebaseUser) => {
			if (!user.email) {
				console.error("Cannot fetch backend user without an email.");
				return;
			}
			const getUserApiCall = () =>
				usersService.getUserByEmail({ email: user.email || "" });

			try {
				const userData = await fetchWithRetry(getUserApiCall, {
					retries: 3,
					initialDelay: 1000,
					isRetryableError: (error: unknown) => {
						return (
							(error as { response?: { status?: number } })?.response
								?.status === 404
						);
					},
				});
				if (userData) {
					setBackendUser(userData);
					localStorage.setItem(
						"user",
						JSON.stringify({
							...userData,
							isAuthenticated: true,
						}),
					);

					// Sync language preference with cookie
					syncLanguagePreference(userData.preferredLanguage);
				} else {
					// This block will be hit if all retries fail
					console.error(
						"Could not retrieve backend user data even after retries. The user might not exist in the backend database.",
					);
					// await signOut();
				}
			} catch (error) {
				console.error(
					"An unexpected error occurred while fetching user data:",
					error,
				);
			}
		},
		[syncLanguagePreference],
	);

	// Function to refresh user data
	const refreshUserData = async () => {
		if (currentUser) {
			await fetchUserData(currentUser);
		}
	};

	// Sign out function
	const signOut = async () => {
		try {
			await authService.signOut();
			setCurrentUser(null);
			setBackendUser(null);
			setToken(null);
			localStorage.removeItem("user");
		} catch (error) {
			console.error("Error signing out:", error);
		}
	};

	// Subscribe to auth state changes
	useEffect(() => {
		const unsubscribe = onAuthStateChanged(auth, async (user) => {
			setCurrentUser(user);

			if (user) {
				posthog?.identify(user.email || user.uid, {
					email: user.email,
				});
				// Get token
				const idToken = await user.getIdToken();
				setToken(idToken);

				// Fetch user data from backend
				await fetchUserData(user);
			} else {
				setBackendUser(null);
				setToken(null);
				// Check localStorage for persisted session
				try {
					const storedUser = localStorage.getItem("user");
					if (storedUser) {
						const parsedUser = JSON.parse(storedUser);
						if (parsedUser.isAuthenticated) {
							// If we have authenticated user in localStorage but Firebase session expired,
							// sign out completely to clear the state
							localStorage.removeItem("user");
						}
					}
				} catch (e) {
					console.error("Error reading from localStorage", e);
				}
			}

			setLoading(false);
		});

		// Cleanup subscription
		return () => unsubscribe();
	}, [fetchUserData, posthog]);

	// Context value
	const value = {
		currentUser,
		backendUser,
		loading,
		token,
		signOut,
		getUserToken,
		refreshUserData,
	};

	return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// Hook for accessing the auth context
export function useAuth() {
	return useContext(AuthContext);
}
